// Cloud loader for the Buddha Game.
// Creates a soft, glowing cloud that floats above the Buddha as the goal destination.
// Uses 3D spheres to create a fluffy, volumetric cloud appearance.

import {
  Scene,
  Mesh,
  SphereGeometry,
  MeshBasicMaterial,
  Material,
  Group,
  Color,
  AdditiveBlending,
  Object3D,
} from 'three';
// Box3/Vector3 were used by GLB auto-scaling — removed when heart model was removed

// ============================================================================
// CLOUD CONFIGURATION
// ============================================================================

const CLOUD_CONFIG = {
  // Position - slightly above Buddha's reach, visible in camera
  // Lowered so the model sits closer to the Buddha and is fully visible
  // Reduced slightly to give more headroom for the pumped heart above
  positionY: 1.6,
  positionX: 0.2,
  positionZ: 0.5,

  // Scale - starting scale applied to the loaded model or procedural cloud
  // Reduced a touch so the cloud and loaded model don't feel oversized
  scale: 0.01,

  // Animation
  floatAmplitude: 0.06,
  floatSpeed: 0.4,

  // Colors - softer, more cloud-like
  baseColor: 0xffffff,
  glowColor: 0xfff8e8,
};

// Cloud puff definitions - each sphere that makes up the cloud
// { x, y, z, radius, opacity }
const CLOUD_PUFFS = [
  // Main body - center puffs (larger, fluffier)
  { x: 0, y: 0, z: 0, r: 0.5, o: 0.85 },
  { x: -0.35, y: 0.08, z: 0.1, r: 0.42, o: 0.8 },
  { x: 0.38, y: 0.05, z: 0.08, r: 0.44, o: 0.8 },

  // Top puffs (billowy top)
  { x: 0.05, y: 0.28, z: 0.05, r: 0.35, o: 0.82 },
  { x: -0.22, y: 0.22, z: 0, r: 0.28, o: 0.78 },
  { x: 0.25, y: 0.2, z: 0, r: 0.3, o: 0.78 },

  // Side extensions (wispy edges)
  { x: -0.7, y: -0.05, z: 0, r: 0.32, o: 0.7 },
  { x: 0.72, y: -0.03, z: 0, r: 0.34, o: 0.7 },
  { x: -0.95, y: -0.1, z: 0, r: 0.22, o: 0.5 },
  { x: 0.98, y: -0.08, z: 0, r: 0.24, o: 0.5 },

  // Bottom puffs (flatter bottom, more solid)
  { x: 0, y: -0.18, z: 0, r: 0.45, o: 0.75 },
  { x: -0.4, y: -0.15, z: 0, r: 0.32, o: 0.7 },
  { x: 0.42, y: -0.14, z: 0, r: 0.34, o: 0.7 },

  // Depth puffs (front and back for volume)
  { x: 0.12, y: 0.08, z: 0.25, r: 0.28, o: 0.55 },
  { x: -0.18, y: 0.04, z: 0.2, r: 0.25, o: 0.55 },
  { x: 0, y: 0.02, z: -0.15, r: 0.35, o: 0.45 },
];

// ============================================================================
// CLOUD STATE
// ============================================================================

interface CloudState {
  group: Group | null;
  modelRoot: Object3D | null;
  puffMeshes: Mesh[];
  initialized: boolean;
  baseY: number;
  glowIntensity: number;
}

const cloudState: CloudState = {
  group: null,
  modelRoot: null,
  puffMeshes: [],
  initialized: false,
  baseY: CLOUD_CONFIG.positionY,
  glowIntensity: 0,
};

// ============================================================================
// CLOUD CREATION
// ============================================================================

/**
 * Create a single cloud puff (sphere with soft material).
 */
function createPuff(
  x: number,
  y: number,
  z: number,
  radius: number,
  opacity: number
): Mesh {
  const geometry = new SphereGeometry(radius, 16, 12);

  const material = new MeshBasicMaterial({
    color: new Color(CLOUD_CONFIG.baseColor),
    transparent: true,
    opacity: opacity,
    depthWrite: false,
  });

  const mesh = new Mesh(geometry, material);
  mesh.position.set(x, y, z);

  return mesh;
}

/**
 * Create the complete cloud from multiple puffs.
 */
function createCloudMeshes(): Group {
  const group = new Group();
  cloudState.puffMeshes = [];

  // Create each puff
  CLOUD_PUFFS.forEach((puff) => {
    const mesh = createPuff(puff.x, puff.y, puff.z, puff.r, puff.o);
    group.add(mesh);
    cloudState.puffMeshes.push(mesh);
  });

  // Add a soft glow sphere behind the cloud
  const glowGeometry = new SphereGeometry(1.0, 16, 12);
  const glowMaterial = new MeshBasicMaterial({
    color: new Color(CLOUD_CONFIG.glowColor),
    transparent: true,
    opacity: 0.12,
    depthWrite: false,
    blending: AdditiveBlending,
  });
  const glowMesh = new Mesh(glowGeometry, glowMaterial);
  glowMesh.position.z = -0.3;
  glowMesh.scale.set(1.6, 0.9, 1);
  group.add(glowMesh);
  cloudState.puffMeshes.push(glowMesh);

  // Apply overall scale
  group.scale.setScalar(CLOUD_CONFIG.scale);

  // Position the group
  group.position.set(
    CLOUD_CONFIG.positionX,
    CLOUD_CONFIG.positionY,
    CLOUD_CONFIG.positionZ
  );

  cloudState.group = group;
  cloudState.baseY = CLOUD_CONFIG.positionY;

  return group;
}

/**
 * Dispose material helper - works with single or array materials safely.
 */
function tryDisposeMaterial(mat?: Material | Material[] | null): void {
  if (!mat) return;
  const mats = Array.isArray(mat) ? mat : [mat];
  for (const m of mats) {
    if (
      'dispose' in m &&
      typeof (m as unknown as { dispose?: () => void }).dispose === 'function'
    ) {
      (m as unknown as { dispose: () => void }).dispose();
    }
  }
}

/**
 * Load a GLB model from the public folder and add a small wrapper group
 * so the rest of the scene can treat it like the old cloud group.
 */
async function loadModelAsCloud(): Promise<Group> {
  // Heart/GLB model support removed — always use procedural cloud meshes
  return createCloudMeshes();
}

// ============================================================================
// CLOUD LOADER
// ============================================================================

/**
 * Load the cloud into the scene.
 */
export function loadCloud(scene: Scene): {
  group: Group;
  dispose: () => void;
} {
  if (cloudState.initialized && cloudState.group) {
    // eslint-disable-next-line no-console
    console.debug('loadCloud: cloud already loaded');
    return {
      group: cloudState.group,
      dispose: () => disposeCloud(scene),
    };
  }

  // Prefer loading the GLB model and return immediately with the group.
  // The model loader is asynchronous — use promise-style load but keep
  // the synchronous API by placing the model/group into the scene when
  // it's ready. We'll create a wrapper group now so callers get an object
  // back immediately.
  const wrapper = new Group();
  wrapper.position.set(
    CLOUD_CONFIG.positionX,
    CLOUD_CONFIG.positionY,
    CLOUD_CONFIG.positionZ
  );
  wrapper.scale.setScalar(CLOUD_CONFIG.scale);
  scene.add(wrapper);

  // Load model async and attach to wrapper. If it fails we create the
  // procedural cloud instead.
  void (async () => {
    const realGroup = await loadModelAsCloud();
    // Remove any previous children placed in wrapper and copy children
    wrapper.clear();
    wrapper.add(...realGroup.children);
    cloudState.group = wrapper;
    cloudState.initialized = true;
    // ensure baseY matches config
    cloudState.baseY = CLOUD_CONFIG.positionY;
  })();

  cloudState.group = wrapper;
  // note: initialization will be marked true after model loads, but provide
  // an immediate group that the rest of the scene can use.

  // eslint-disable-next-line no-console
  console.debug(
    'loadCloud: 3D cloud created with',
    CLOUD_PUFFS.length,
    'puffs'
  );

  return {
    group: wrapper,
    dispose: () => disposeCloud(scene),
  };
}

/**
 * Dispose of the cloud and clean up resources.
 */
function disposeCloud(scene: Scene): void {
  if (!cloudState.group) return;

  try {
    scene.remove(cloudState.group);

    // Dispose any procedural puff meshes
    cloudState.puffMeshes.forEach((mesh) => {
      try {
        mesh.geometry.dispose();
        (mesh.material as MeshBasicMaterial).dispose();
      } catch (e) {
        void e;
      }
    });

    // Dispose model resources if present
    if (cloudState.modelRoot) {
      cloudState.modelRoot.traverse((obj: Object3D) => {
        if ('isMesh' in obj && (obj as Mesh).isMesh) {
          try {
            const m = obj as Mesh;
            m.geometry?.dispose?.();
            tryDisposeMaterial(m.material);
          } catch (e) {
            void e;
          }
        }
      });
      cloudState.modelRoot = null;
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('loadCloud: error disposing cloud', e);
  }

  cloudState.group = null;
  cloudState.puffMeshes = [];
  cloudState.initialized = false;
  cloudState.glowIntensity = 0;

  // eslint-disable-next-line no-console
  console.debug('loadCloud: cloud disposed');
}

// ============================================================================
// CLOUD ANIMATION
// ============================================================================

/**
 * Update the cloud's floating animation.
 */
export function updateCloudFloat(elapsed: number): void {
  if (!cloudState.group) return;

  // Gentle up/down bobbing
  const floatOffset =
    Math.sin(elapsed * CLOUD_CONFIG.floatSpeed * Math.PI * 2) *
    CLOUD_CONFIG.floatAmplitude;

  cloudState.group.position.y = cloudState.baseY + floatOffset;

  // Subtle horizontal drift
  const driftX = Math.sin(elapsed * 0.15) * 0.02;
  cloudState.group.position.x = CLOUD_CONFIG.positionX + driftX;

  // Subtle breathing scale (on top of base scale)
  const baseScale = CLOUD_CONFIG.scale;
  const scaleBreath = baseScale * (1 + Math.sin(elapsed * 0.5) * 0.03);
  cloudState.group.scale.setScalar(scaleBreath);
}

/**
 * Set the cloud's glow intensity (0-1).
 */
export function setCloudGlowIntensity(intensity: number): void {
  cloudState.glowIntensity = Math.max(0, Math.min(1, intensity));
  // If procedural puffs exist, tint/brighten them like before
  if (cloudState.puffMeshes.length > 0) {
    cloudState.puffMeshes.forEach((mesh, i) => {
      const material = mesh.material as MeshBasicMaterial;
      const puff = CLOUD_PUFFS[i];

      if (puff) {
        const baseOp = puff.o;
        material.opacity = baseOp + (1 - baseOp) * intensity * 0.5;
        const warmth = intensity * 0.15;
        material.color.setRGB(1, 1 - warmth * 0.1, 1 - warmth * 0.2);
      } else {
        material.opacity = 0.15 + intensity * 0.4;
      }
    });
    return;
  }

  // Otherwise, apply a subtle emissive-like tint to the model meshes
  if (cloudState.modelRoot) {
    cloudState.modelRoot.traverse((obj: Object3D) => {
      if ('isMesh' in obj && (obj as Mesh).isMesh) {
        try {
          const mObj = obj as Mesh;
          const mat = mObj.material as Material | Material[] | undefined;
          if (!mat) return;
          const mats = Array.isArray(mat) ? mat : [mat];
          for (const mm of mats) {
            const maybe = mm as unknown as {
              color?: { r: number; g: number; b: number };
              opacity?: number;
              transparent?: boolean;
              needsUpdate?: boolean;
            };
            if (maybe.color) {
              const warmth = intensity * 0.15;
              maybe.color.r = Math.min(1, maybe.color.r + warmth * 0.05);
              maybe.color.g = Math.max(0, maybe.color.g - warmth * 0.02);
              maybe.color.b = Math.max(0, maybe.color.b - warmth * 0.03);
              maybe.opacity = Math.min(
                1,
                (maybe.opacity ?? 1) + intensity * 0.2
              );
              maybe.transparent = (maybe.opacity ?? 1) < 1;
              maybe.needsUpdate = true;
            }
          }
        } catch (e) {
          void e;
        }
      }
    });
  }
}

/**
 * Update win glow animation.
 */
export function updateWinGlow(progress: number): void {
  const easedProgress = 1 - Math.pow(1 - progress, 3);
  setCloudGlowIntensity(easedProgress);
}

/**
 * Reset the cloud to initial state.
 */
export function resetCloud(): void {
  setCloudGlowIntensity(0);
  if (cloudState.group) {
    cloudState.group.position.y = cloudState.baseY;
    cloudState.group.scale.setScalar(1);
  }

  cloudState.puffMeshes.forEach((mesh, i) => {
    const material = mesh.material as MeshBasicMaterial;
    const puff = CLOUD_PUFFS[i];
    material.color.setHex(
      puff ? CLOUD_CONFIG.baseColor : CLOUD_CONFIG.glowColor
    );
    if (puff) {
      material.opacity = puff.o;
    } else {
      material.opacity = 0.15;
    }
  });
}

/**
 * Get the cloud's Y position.
 */
export function getCloudY(): number {
  return cloudState.baseY;
}

/**
 * Get cloud state for debugging.
 */
export function getCloudState(): {
  initialized: boolean;
  baseY: number;
  glowIntensity: number;
} {
  return {
    initialized: cloudState.initialized,
    baseY: cloudState.baseY,
    glowIntensity: cloudState.glowIntensity,
  };
}
export function getHeartModel(): Object3D | null {
  return null;
}

export default {
  loadCloud,
  updateCloudFloat,
  setCloudGlowIntensity,
  updateWinGlow,
  resetCloud,
  getCloudY,
  getCloudState,
  getHeartModel,
};
